/**
 * PauseMenuState — pure, framework-free state machine for the pause overlay.
 *
 * Tracks which sub-screen is visible (pause | options) and the current option
 * values. The Phaser PauseScene owns rendering and calls these methods in
 * response to input events.
 *
 * No Phaser import — safe to unit-test in Node.
 */

import type { GameOptions } from '@/systems/SaveManager';
import type { BloodLevel } from '@/systems/HitJuice';

export type PauseScreen = 'pause' | 'options';
export type VolumeKey = 'masterVolume' | 'musicVolume' | 'sfxVolume' | 'voiceVolume';

const VOLUME_STEP = 0.1;
const VOLUME_MAX = 1;
const VOLUME_MIN = 0;

export class PauseMenuState {
  private _screen: PauseScreen = 'pause';
  private _opts: GameOptions;

  constructor(opts: GameOptions) {
    this._opts = { ...opts };
  }

  get screen(): PauseScreen {
    return this._screen;
  }

  /** Snapshot of current options (copy — mutate the state via methods). */
  get opts(): Readonly<GameOptions> {
    return { ...this._opts };
  }

  enterOptions(): void {
    this._screen = 'options';
  }

  exitOptions(): void {
    this._screen = 'pause';
  }

  /**
   * Nudge a volume field by delta (±VOLUME_STEP), clamped [0, 1], rounded to
   * 1 decimal place to avoid floating-point drift. Returns the new value.
   * Handles optional fields (masterVolume) by defaulting to 1.0 if undefined.
   */
  nudgeVolume(key: VolumeKey, delta: number): number {
    const current = this._opts[key] as number | undefined;
    const safe = current !== undefined && !Number.isNaN(current) ? current : 1.0;
    const raw = safe + delta;
    const clamped = Math.max(VOLUME_MIN, Math.min(VOLUME_MAX, raw));
    const precise = Math.round(clamped / VOLUME_STEP) * VOLUME_STEP;
    const next = { ...this._opts };
    next[key] = Math.round(precise * 10) / 10;
    this._opts = next;
    return next[key] as number;
  }

  /** Toggle master mute. Returns the new muted state. */
  toggleMasterMute(): boolean {
    const muted = !(this._opts.masterMuted ?? false);
    this._opts = { ...this._opts, masterMuted: muted };
    return muted;
  }

  /** Toggle blood effects. Returns the new value. */
  toggleBlood(): boolean {
    this._opts = { ...this._opts, bloodEnabled: !this._opts.bloodEnabled };
    return this._opts.bloodEnabled;
  }

  /** Toggle screen shake. Returns the new value. */
  toggleScreenShake(): boolean {
    this._opts = { ...this._opts, screenShakeEnabled: !this._opts.screenShakeEnabled };
    return this._opts.screenShakeEnabled;
  }

  /**
   * Cycle blood-gore fidelity: off → normal → gore → off.
   * Returns the new BloodLevel.
   */
  cycleBloodLevel(): BloodLevel {
    const order: BloodLevel[] = ['off', 'normal', 'gore'];
    const current = this._opts.bloodLevel ?? 'normal';
    const next = order[(order.indexOf(current) + 1) % order.length];
    this._opts = { ...this._opts, bloodLevel: next };
    return next;
  }
}
